import type { EnvironmentsSourceWithID } from '@customerio/hydra-ui-api';
import { ACTIVITY_LOG_SOURCE } from '@/utils/activity-log-source-types';
import { getSourceIconInfo } from './sources';

/**
 * The Sources filter's options.
 *
 * The backend answers with one row per source it has seen, so a workspace that
 * has run fifty campaigns offers fifty campaign sources. Nobody wants fifty
 * checkboxes that all say the same thing, so sources that would display the
 * same name become one option standing for all of their ids, and choosing it
 * asks for every one of them.
 */

/** What a workspace calls the two source families that are named after a product. */
export type SourceGroupTerms = {
  campaignsPlural: string;
  newsletterSingular: string;
};

/** One option in the Sources filter: a name, and every source id it stands for. */
export type SourceFilterGroup = {
  label: string;
  sourceIds: number[];
};

/**
 * What to call one source in the filter.
 *
 * A source that names a campaign is a campaign however it was sent, which the
 * icon table cannot tell because it only sees the type. Newsletters are named
 * for the same reason: the workspace's own noun beats the generic one.
 */
function groupLabelOf(source: EnvironmentsSourceWithID, terms: SourceGroupTerms): string {
  if (source.campaign_name != null) {
    return terms.campaignsPlural;
  }
  if (source.type === ACTIVITY_LOG_SOURCE.Newsletter) {
    return terms.newsletterSingular;
  }
  return getSourceIconInfo(source).title;
}

/** The filter's options, in alphabetical order, one per distinct name. */
export function groupSourceFilters(
  sources: readonly EnvironmentsSourceWithID[],
  terms: SourceGroupTerms,
): SourceFilterGroup[] {
  const groups = new Map<string, SourceFilterGroup>();

  for (const source of sources) {
    const label = groupLabelOf(source, terms);
    const group = groups.get(label);
    if (group == null) {
      groups.set(label, { label, sourceIds: [source.id] });
    } else {
      group.sourceIds.push(source.id);
    }
  }

  return [...groups.values()].sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * The value that stands for one option. A group is its ids, so the ids are
 * what identifies it: two workspaces never agree on which sources share a
 * name, and the name itself is display text.
 */
export function sourceGroupValue(group: SourceFilterGroup): string {
  return group.sourceIds.join(',');
}

/**
 * Which options the chosen source ids add up to.
 *
 * Any of an option's ids is enough. Choosing one writes all of them, so a
 * partial match means the ids were written when the option stood for fewer
 * sources than it does now — which happens to any link once the workspace
 * writes from one more campaign. Requiring all of them would leave that link
 * narrowing the list with nothing in the bar to say so, and no way to clear
 * it; ticking the option again rewrites the whole group.
 */
export function chosenSourceGroups(groups: readonly SourceFilterGroup[], sourceIds: readonly number[]): string[] {
  const chosen = new Set(sourceIds);
  return groups.filter((group) => group.sourceIds.some((id) => chosen.has(id))).map((group) => sourceGroupValue(group));
}

/** The source ids the chosen options stand for. An option nobody offers is ignored. */
export function sourceIdsOfGroups(groups: readonly SourceFilterGroup[], values: readonly string[]): number[] {
  const wanted = new Set(values);
  return groups.filter((group) => wanted.has(sourceGroupValue(group))).flatMap((group) => group.sourceIds);
}
